import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/states';
import { getToken } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useProfileLocale } from '@/lib/hooks';
import { isLandingHost } from '@/lib/site';
import { ActivityPage } from '@/routes/activity';
import { AgentDetailPage } from '@/routes/agent-detail';
import { AgentsPage } from '@/routes/agents';
import { AccountDesksPage } from '@/routes/account-desks';
import { AccountShell, AppShell } from '@/routes/app-shell';
import { GuestDeskPage } from '@/routes/guest-desk';
import { SharedDeskPage } from '@/routes/shared-desk';
import { ConnectionDetailPage } from '@/routes/connection-detail';
import { ConversationPage } from '@/routes/conversation';
import { ConnectionsPage } from '@/routes/connections';
import { SkillDetailPage } from '@/routes/skill-detail';
import { SkillsPage } from '@/routes/skills';
import { DatabasePage } from '@/routes/database';
import { ViewDetailPage } from '@/routes/view-detail';
import { ViewsPage } from '@/routes/views';
import { DeskDetailPage } from '@/routes/desk-detail';
import { DesksPage } from '@/routes/desks';
import { DeskStorePage } from '@/routes/desk-store';
import { ConnectPage } from '@/routes/connect';
import { IndexRedirect } from '@/routes/index-redirect';
import { InboxPage } from '@/routes/inbox';
import { LandingPage } from '@/routes/landing';
import { PrivacyPage, TermsPage } from '@/routes/legal';
import { LoginPage } from '@/routes/login';
import { OverviewPage } from '@/routes/overview';
import { RegisterPage } from '@/routes/register';
import { SessionsPage } from '@/routes/sessions';
import { ReportsPage } from '@/routes/reports';
import { AccountStartPage, StartPage } from '@/routes/start';
import { FrontDeskPage } from '@/routes/front-desk';
import { VickiKnowledgePage } from '@/routes/vicki-knowledge';
import { SettingsPage } from '@/routes/settings';
import { UsagePage } from '@/routes/usage';
import { WorkItemDetailPage } from '@/routes/work-item-detail';
import { WorkItemsPage } from '@/routes/work-items';

export interface RouterContext {
  queryClient: QueryClient;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="flex min-h-full items-center justify-center p-6">
      <EmptyState
        title={t('This page doesn’t exist')}
        description={t('The link may have changed, or the item was deleted.')}
        action={
          <Button asChild variant="outline">
            <Link to="/">{t('Go to start page')}</Link>
          </Button>
        }
      />
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-xl p-8">
      <ErrorState title={t('Something went wrong')} error={error} onRetry={reset} />
    </div>
  ),
});

export interface LoginSearch {
  redirect?: string;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({ redirect: str(s.redirect) }),
  beforeLoad: () => {
    if (getToken()) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: () => {
    if (getToken()) throw redirect({ to: '/' });
  },
  component: RegisterPage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  beforeLoad: ({ location }) => {
    if (!getToken()) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AuthedRoot,
});

function AuthedRoot() {
  useProfileLocale();
  return <Outlet />;
}

/** `/`: the landing page on https://deskworks.ai and for visitors who are not signed in; the desks for signed-in people elsewhere. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (isLandingHost() || !getToken() ? <LandingPage /> : <IndexRedirect />),
});

/** The landing page on any host, e.g. to look at it on dev.deskworks.ai or locally. */
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/landing',
  component: LandingPage,
});

/** Privacy policy and terms: public on every host (also deskworks.ai, src/lib/site.ts). */
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms-and-conditions',
  component: TermsPage,
});

/** A desk shared by link (docs/features/desk-sharing.md): joining it, and the desk as its guest sees it; no sign-in needed. */
const sharedDeskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$token',
  component: SharedDeskPage,
});

const guestDeskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/$deskId',
  component: GuestDeskPage,
});

/**
 * Account-level pages: `/$account/...` where `$account` is the account slug (or id).
 * Static first segments (`/login`, `/w/...`) always win over this param route in TanStack Router's ranking,
 * so an account slug of `w` or `login` would be shadowed.
 */
const accountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '$account',
  component: AccountShell,
});

const accountIndexRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    // The Front Desk is the account's start page (docs/features/front-desk.md).
    throw redirect({ to: '/$account/front-desk', params: { account: params.account }, replace: true });
  },
});

const accountDesksRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'desks',
  component: AccountDesksPage,
});

const accountInboxRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'inbox',
  component: InboxPage,
});

export interface AccountStartSearch {
  /** The setup chat to show. */
  chat?: string;
  /** The workspace a new chat lives in (default: the last one used). */
  workspace?: string;
}

export interface FrontDeskSearch {
  /** The chat with VICKI to show. */
  chat?: string;
  /** A first message to start a new chat with (e.g. from a desk's "Change with VICKI"). */
  message?: string;
}

/** The Front Desk: chats with VICKI, everyone of the account sees them (docs/features/front-desk.md). */
const accountFrontDeskRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'front-desk',
  validateSearch: (s: Record<string, unknown>): FrontDeskSearch => ({ chat: str(s.chat), message: str(s.message) }),
  component: FrontDeskPage,
});

/** What VICKI knows: her memory, skills, notes and journal. */
const accountVickiKnowledgeRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'front-desk/knowledge',
  component: VickiKnowledgePage,
});

/** The Cockpit became the Front Desk: old links land there. */
const accountCockpitRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'cockpit',
  beforeLoad: ({ params, search }) => {
    throw redirect({ to: '/$account/front-desk', params: { account: params.account }, search: { chat: str((search as Record<string, unknown>).chat) }, replace: true });
  },
});

/** Setup is one of VICKI's skills now: the page only shows earlier setup chats; a new one starts at the Front Desk. */
const accountStartRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'start',
  validateSearch: (s: Record<string, unknown>): AccountStartSearch => ({ chat: str(s.chat), workspace: str(s.workspace) }),
  beforeLoad: ({ params, search }) => {
    if (!search.chat) throw redirect({ to: '/$account/front-desk', params: { account: params.account }, search: {}, replace: true });
  },
  component: AccountStartPage,
});

const workspaceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'w/$workspaceId',
  component: AppShell,
});

const overviewRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/',
  component: OverviewPage,
});

export interface StartSearch {
  /** The setup chat to show. */
  chat?: string;
  /** "Change this desk": a new chat starts with this desk's context. */
  desk?: string;
}

const startRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'start',
  validateSearch: (s: Record<string, unknown>): StartSearch => ({ chat: str(s.chat), desk: str(s.desk) }),
  component: StartPage,
});

export interface DatabaseSearch {
  /** "schema.table" */
  table?: string;
  view?: 'sql' | 'api';
}

const databaseRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'database',
  validateSearch: (s: Record<string, unknown>): DatabaseSearch => ({ table: str(s.table), view: s.view === 'sql' || s.view === 'api' ? s.view : undefined }),
  component: DatabasePage,
});

const viewsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'views',
  component: ViewsPage,
});

const viewDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'views/$viewId',
  component: ViewDetailPage,
});

const desksRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'desks',
  component: DesksPage,
});

const deskStoreRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'desk-store',
  component: DeskStorePage,
});

export type DeskTab = 'work' | 'chat' | 'files' | 'training' | 'setup' | 'history';
const DESK_TABS: readonly string[] = ['work', 'chat', 'files', 'training', 'setup', 'history'];

const deskDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'desks/$deskId',
  // chat: the conversation open on the Chats tab. The former Conversation tab lands on Chats.
  // status: the filter on the Work tab. file: the file open on the Files tab.
  validateSearch: (s: Record<string, unknown>): { tab?: DeskTab; chat?: string; status?: string; file?: string } => {
    const tab = str(s.tab) === 'conversation' ? 'chat' : str(s.tab);
    return { tab: tab && DESK_TABS.includes(tab) ? (tab as DeskTab) : undefined, chat: str(s.chat), status: str(s.status), file: str(s.file) };
  },
  component: DeskDetailPage,
});

export interface WorkItemsSearch {
  status?: string;
  deskId?: string;
  agentId?: string;
  q?: string;
}

const workItemsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'work-items',
  validateSearch: (s: Record<string, unknown>): WorkItemsSearch => ({
    status: str(s.status),
    deskId: str(s.deskId),
    agentId: str(s.agentId),
    q: str(s.q),
  }),
  component: WorkItemsPage,
});

const workItemDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'work-items/$workItemId',
  component: WorkItemDetailPage,
});

const conversationRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'conversations/$conversationId',
  component: ConversationPage,
});

const agentsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'agents',
  component: AgentsPage,
});

const agentDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'agents/$agentId',
  component: AgentDetailPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'connections',
  component: ConnectionsPage,
});

const connectionDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'connections/$connectionId',
  component: ConnectionDetailPage,
});

const skillsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'skills',
  component: SkillsPage,
});

const skillDetailRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'skills/$skillId',
  component: SkillDetailPage,
});

export interface ActivitySearch {
  deskId?: string;
}

const activityRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'activity',
  validateSearch: (s: Record<string, unknown>): ActivitySearch => ({ deskId: str(s.deskId) }),
  component: ActivityPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'reports',
  validateSearch: (s: Record<string, unknown>): { deskId?: string } => ({ deskId: str(s.deskId) }),
  component: ReportsPage,
});

export interface SessionsSearch {
  session?: string;
  deskId?: string;
}

const sessionsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'sessions',
  validateSearch: (s: Record<string, unknown>): SessionsSearch => ({ session: str(s.session), deskId: str(s.deskId) }),
  component: SessionsPage,
});

export type UsageRange = '7d' | '30d' | '90d' | 'month' | 'last-month';
const USAGE_RANGES: UsageRange[] = ['7d', '30d', '90d', 'month', 'last-month'];

const usageRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'usage',
  validateSearch: (s: Record<string, unknown>): { range?: UsageRange; metric?: 'cost' | 'tokens' } => {
    const range = str(s.range) as UsageRange | undefined;
    const metric = str(s.metric);
    return { range: range && USAGE_RANGES.includes(range) ? range : undefined, metric: metric === 'cost' || metric === 'tokens' ? metric : undefined };
  },
  component: UsagePage,
});

export type SettingsTab = 'workspace' | 'runtime-profiles' | 'account' | 'people' | 'vicki' | 'mcp';
const SETTINGS_TABS: SettingsTab[] = ['workspace', 'runtime-profiles', 'account', 'people', 'vicki', 'mcp'];

const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'settings',
  validateSearch: (s: Record<string, unknown>): { tab?: SettingsTab } => {
    const tab = str(s.tab) as SettingsTab | undefined;
    return { tab: tab && SETTINGS_TABS.includes(tab) ? tab : undefined };
  },
  component: SettingsPage,
});

/** An app asks for access to Deskworks's MCP server by OAuth (src/routes/connect.tsx); signing in comes first. */
const connectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'connect',
  validateSearch: (s: Record<string, unknown>): { request?: string } => ({ request: str(s.request) }),
  component: ConnectPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  landingRoute,
  privacyRoute,
  termsRoute,
  sharedDeskRoute,
  guestDeskRoute,
  indexRoute,
  authedRoute.addChildren([
    connectRoute,
    accountRoute.addChildren([
      accountIndexRoute,
      accountDesksRoute,
      accountInboxRoute,
      accountStartRoute,
      accountFrontDeskRoute,
      accountVickiKnowledgeRoute,
      accountCockpitRoute,
    ]),
    workspaceRoute.addChildren([
      overviewRoute,
      startRoute,
      desksRoute,
      deskStoreRoute,
      deskDetailRoute,
      workItemsRoute,
      workItemDetailRoute,
      reportsRoute,
      conversationRoute,
      agentsRoute,
      agentDetailRoute,
      connectionsRoute,
      connectionDetailRoute,
      databaseRoute,
      viewsRoute,
      viewDetailRoute,
      skillsRoute,
      skillDetailRoute,
      activityRoute,
      sessionsRoute,
      usageRoute,
      settingsRoute,
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
